import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { ROUTE_BUILDER } from '@/constants/pages';
import { getContainmentPolicy } from '@/helpers/containmentPolicy';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import BookingFlowPage from './BookingFlowPage';
import { useBookingFlowRedesign } from './useBookingFlowRedesign';
import { useFlagResolveTimeout } from './useFlagResolveTimeout';

type BookingFlowRouteParams = { placeSlugId: string };

/**
 * Gates a booking-flow step page behind the `booking-flow-redesign` Confidence flag.
 *
 * - Flag resolves ON  -> renders the wrapped component.
 * - Flag resolves OFF -> replaces (never pushes) to the place page, preserving the query
 *   string verbatim, so back navigation isn't trapped.
 * - Not yet resolved   -> renders the BookingFlowPage shell with a spinner, never blank,
 *   never redirects. Bounded by FLAG_READY_TIMEOUT_MS: `isReady` can otherwise stay false
 *   forever if the Confidence client never becomes ready (see useConfidenceFlag.tsx) — once
 *   the timeout elapses unresolved, the visitor is treated as control and redirected.
 *
 * A contained flow (see the booking-widget containment policy) bypasses the gate entirely:
 * the flag is an A/B between the modal and these pages on bokadirekt.se, and a contained
 * flow ships only these pages, so it has no control arm — and the control redirect targets
 * the place page, which does not exist under basename /embed. The bypass also skips the
 * flag-resolution wait (a third-party iframe commonly has no tracking consent, so control
 * would otherwise be the likely widget path).
 *
 * The resolve-once/persist mechanics live in useBookingFlowRedesign, shared with the
 * place-page entry points (KTD7).
 */
const withBookingFlowRedesignFlag = <P extends object>(Component: React.ComponentType<P>) => {
  const Gated = (props: P) => {
    const { placeSlugId } = useParams<BookingFlowRouteParams>();
    const location = useLocation();

    const { enabled, isResolved } = useBookingFlowRedesign();

    const timedOut = useFlagResolveTimeout(isResolved);

    if (isResolved) {
      if (!enabled) {
        return <Redirect to={{ pathname: ROUTE_BUILDER.placePage(placeSlugId), search: location.search }} />;
      }
      return <Component {...props} />;
    }

    if (timedOut) {
      return <Redirect to={{ pathname: ROUTE_BUILDER.placePage(placeSlugId), search: location.search }} />;
    }

    return (
      <BookingFlowPage title="">
        <LoadingPlaceHolder />
      </BookingFlowPage>
    );
  };

  // Two components, not an early return: Gated's hooks never mount in a
  // contained flow, so no flag fetch and no armed redirect timer exist to leak.
  return (props: P) => (getContainmentPolicy().bypassesRedesignFlag ? <Component {...props} /> : <Gated {...props} />);
};

export default withBookingFlowRedesignFlag;
